import React, { useCallback, useEffect, useRef, useState } from 'react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CircularProgress from '@mui/material/CircularProgress'
import styled from 'styled-components'
import NextButton from 'features/createBp/components/atoms/NextButton'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { clearCart } from 'utils/localStorage'
import { confirmStripeCheckoutSession } from 'services/BpService'

const MAX_CONFIRMATION_ATTEMPTS = 15
const CONFIRMATION_RETRY_DELAY_MS = 2000

const PaymentSuccess = () => {
  const [status, setStatus] = useState('processing')
  const [message, setMessage] = useState('Confirming your payment...')
  const retryTimeout = useRef(null)

  const confirmPayment = useCallback(async (attempt = 1) => {
    const checkoutSessionId = new URLSearchParams(window.location.search)
      .get('session_id')

    if (!checkoutSessionId) {
      setStatus('error')
      setMessage('The Checkout Session is missing. Your cart has not been cleared.')
      return
    }

    setStatus('processing')

    try {
      const response = await confirmStripeCheckoutSession(checkoutSessionId)
      const confirmationStatus = response?.data?.data?.status

      if (confirmationStatus === 'confirmed') {
        clearCart()
        setStatus('confirmed')
        setMessage('Payment Successful!')
        return
      }

      if (attempt < MAX_CONFIRMATION_ATTEMPTS) {
        setMessage('Your payment is still processing. This page will update automatically.')
        retryTimeout.current = window.setTimeout(
          () => confirmPayment(attempt + 1),
          CONFIRMATION_RETRY_DELAY_MS
        )
        return
      }

      setStatus('error')
      setMessage('Payment confirmation is taking longer than expected. Please try again.')
    } catch (error) {
      setStatus('error')
      setMessage('We could not confirm the payment. Your cart has not been cleared.')
    }
  }, [])

  useEffect(() => {
    confirmPayment()

    return () => {
      if (retryTimeout.current) {
        window.clearTimeout(retryTimeout.current)
      }
    }
  }, [confirmPayment])

  return (
    <Wrapper>
      {status === 'confirmed' && <CheckIcon />}
      {status === 'processing' && <ProcessingIcon size={120} />}
      {status === 'error' && <ErrorIcon />}
      <Text>{message}</Text>
      {status === 'confirmed' && (
        <NextButton onClick={() => redirect(PRIVATE_PATHS.dashboard)}>
          Back to dashboard
        </NextButton>
      )}
      {status === 'error' && (
        <ButtonGroup>
          <NextButton onClick={() => confirmPayment()}>Try confirmation again</NextButton>
          <NextButton onClick={() => redirect(PRIVATE_PATHS.cart)}>Back to cart</NextButton>
        </ButtonGroup>
      )}
    </Wrapper>
  )
}

export default PaymentSuccess

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 50px;
  flex-direction: column;
  padding-bottom: 50px;
`

const CheckIcon = styled(CheckCircleIcon)`
  font-size: 200px !important;
  color: ${({ theme }) => theme.colors.secondary};

  @media (max-width: 768px) {
    font-size: 120px !important;
  }
`

const ErrorIcon = styled(ErrorOutlineIcon)`
  font-size: 160px !important;
  color: ${({ theme }) => theme.colors.error || '#d32f2f'};
`

const ProcessingIcon = styled(CircularProgress)`
  color: ${({ theme }) => theme.colors.secondary} !important;
`

const Text = styled.div`
  font-size: 34px;
  text-align: center;
  margin: 30px 0 40px;

  @media (max-width: 768px) {
    font-size: 24px;
    margin: 20px 0 30px;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`
